import {Localizer} from '../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {
  AnalyticsDonutMetricValue,
  AnalyticsScalarMetric,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetric,
  AnalyticsTableMetricsEnum,
} from '../../models/api/analyticsMetricsModel';
import {
  AnalyticsComputedGraphMetricBase,
  AnalyticsComputedGraphMetricsEnum,
  AnalyticsComputedProgressMetricBase,
  AnalyticsComputedProgressMetricsEnum,
} from './analyticsMetricsTransformersTypes';
import {csatSurveyResponseHistoToGraph} from './csatSurveyScoreHistoToGraph';
import {firstReplyTimeHistoToGraph} from './firstReplyTimeHistoToGraph';
import {pctMessagesWithMessageTemplatesToProgress} from './pctMessagesWithMessageTemplatesToProgress';
import {pctTaggedConversationsToProgress} from './pctTaggedConversationsToProgress';
import {replyTimeHistoToGraph} from './replyTimeHistoToGraph';
import {SankeyGraphMetricValue, sankeyTableToGraph} from './sankeyTableToGraph';
import {stageAvgResolutionTimeHistoToGraph} from './stageAvgResolutionTimeHistoToGraph';
import {pctStageResolvedSegmentsToProgress} from './stagePctResolvedSegmentsTableToProgress';
import {stageRepliesToResolutionHistoToGraph} from './stageRepliesToResolutionHistoToGraph';
import {WhatHappenedChartSeries, whatHappenedTableToGraph} from './whatHappenedTableToGraph';

/*
 * Types.
 */

export type AnalyticsComputedGraphMetricValue =
  | AnalyticsDonutMetricValue
  | SankeyGraphMetricValue
  | WhatHappenedChartSeries;

export interface AnalyticsComputedGraphMetric
  extends AnalyticsComputedGraphMetricBase<AnalyticsComputedGraphMetricValue> {}

export interface AnalyticsComputedProgressMetric extends AnalyticsComputedProgressMetricBase {}

export type TableToGraphTransformer = (
  metricData: AnalyticsTableMetric | undefined,
  localize: Localizer,
) => AnalyticsComputedGraphMetric;

export type TableToDonutMetricTransformer = (
  id: AnalyticsComputedGraphMetricsEnum,
  metricData: AnalyticsTableMetric | undefined,
  localize: Localizer,
) => AnalyticsComputedGraphMetricBase<AnalyticsDonutMetricValue>;

export type ScalarToProgressTransformer = (
  metricData: AnalyticsScalarMetric | undefined,
  localize: Localizer,
) => AnalyticsComputedProgressMetric;

export type TableToProgressTransformer = (
  metricData: AnalyticsTableMetric | undefined,
  localize: Localizer,
) => AnalyticsComputedProgressMetric;

interface ComputedTableMetricTransformer {
  from: AnalyticsTableMetricsEnum;
  transformer: TableToGraphTransformer;
}
interface ComputedScalarMetricTransformer {
  from: AnalyticsScalarMetricsEnum;
  transformer: ScalarToProgressTransformer;
}

/*
 * Transformers.
 */

export function getComputedMetricTransformer(
  metric: AnalyticsComputedGraphMetricsEnum,
): ComputedTableMetricTransformer;
export function getComputedMetricTransformer(
  metric: AnalyticsComputedProgressMetricsEnum,
): ComputedScalarMetricTransformer;

export function getComputedMetricTransformer(
  metric: AnalyticsComputedGraphMetricsEnum | AnalyticsComputedProgressMetricsEnum,
) {
  switch (metric) {
    case AnalyticsComputedGraphMetricsEnum.WHAT_HAPPENED_GRAPH:
      return {
        from: AnalyticsTableMetricsEnum.WHAT_HAPPENED_TABLE,
        transformer: whatHappenedTableToGraph,
      };
    case AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH:
      return {
        from: AnalyticsTableMetricsEnum.SANKEY_TABLE,
        transformer: sankeyTableToGraph,
      };
    case AnalyticsComputedGraphMetricsEnum.REPLY_TIME_GRAPH:
      return {
        from: AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
        transformer: replyTimeHistoToGraph,
      };
    case AnalyticsComputedGraphMetricsEnum.FIRST_REPLY_TIME_GRAPH:
      return {
        from: AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM,
        transformer: firstReplyTimeHistoToGraph,
      };
    case AnalyticsComputedProgressMetricsEnum.TAG_REPORT_PROGRESS:
      return {
        from: AnalyticsScalarMetricsEnum.PCT_TAGGED_CONVERSATIONS,
        transformer: pctTaggedConversationsToProgress,
      };
    case AnalyticsComputedProgressMetricsEnum.STAGE_RESOLVED_SEGMENTS_PROGRESS:
      return {
        from: AnalyticsTableMetricsEnum.STAGE_PCT_RESOLVED_SEGMENTS_TABLE,
        transformer: pctStageResolvedSegmentsToProgress,
      };
    case AnalyticsComputedProgressMetricsEnum.MESSAGE_TEMPLATE_REPORT_PROGRESS:
      return {
        from: AnalyticsScalarMetricsEnum.PCT_MESSAGES_WITH_MESSAGE_TEMPLATES,
        transformer: pctMessagesWithMessageTemplatesToProgress,
      };
    case AnalyticsComputedGraphMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO_GRAPH:
      return {
        from: AnalyticsTableMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO,
        transformer: csatSurveyResponseHistoToGraph,
      };
    case AnalyticsComputedGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH:
      return {
        from: AnalyticsTableMetricsEnum.STAGE_AVG_RESOLUTION_TIME_HISTO,
        transformer: stageAvgResolutionTimeHistoToGraph,
      };
    case AnalyticsComputedGraphMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_GRAPH:
      return {
        from: AnalyticsTableMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_HISTO,
        transformer: stageRepliesToResolutionHistoToGraph,
      };
    default:
      return null; // Shouldn't happen.
  }
}
